import React, { useEffect, useRef, useState } from "react";

import { useForm } from "../useForm";

import SelectMenu from "../../controls/SelectMenu";
import Pills from "../../controls/Pills";
import {
  conditionTypes,
  lineProductConditionOption,
  orderStatusOptions,
  shipmentStatusPill,
  updateObjectInArray,
} from "../../../shared/utility";
import OrderItemsForShipping from "./OrderItemsForShipping";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import ProductTemperaturePromptModal from "./ProductTemperaturePromptModal";
import { classNames } from "primereact/utils";
import { Accordion, AccordionTab } from "primereact/accordion";
import {
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import Input from "../../controls/Input";
import DatePickerInput from "../../controls/DatePickerInput";
import QuickBooksInvoiceStatus from "./QuickBooksInvoiceStatus";
import { isActiveInvoiceStatus } from "../../../services/quickBooksInvoicesApi";

const initialFValues = {
  id: 0,
  customer_id: 0,
  subtotal_dollar_amount: 0,
  total_dollar_amount: 0,
  order_id: 0,
  orderItems: [],
  truck_temperature: 0,
  truck_condition: { value: 1, label: "Satisfactory" },
};

function normalizeShippingItemsFromBackendOrderItem(backendOrderItem) {
  if (!backendOrderItem) return [];
  if (Array.isArray(backendOrderItem.shippingItems)) {
    return backendOrderItem.shippingItems;
  }

  const shippedCartons = Array.isArray(backendOrderItem.shipped_cartons)
    ? backendOrderItem.shipped_cartons
    : [];

  return shippedCartons.map((carton) => {
    const packagingDate = carton.packaging_date ?? null;

    return {
      id: carton.id,
      barcode: carton.barcode,
      lot_number: carton.lot_number,
      packing_size: carton.packing_size,
      packaging_date: packagingDate,
      carton_count: carton.carton_count,
      carton_type: carton.carton_type,
      product_id: backendOrderItem.product_id,
      product_name: carton.product_name,
      plu: carton.plu,
      serial_number: carton.serial_number,
      best_before_date: carton.best_before_date,
      statustatus: carton.statustatus,
      description_en: carton.description_en,
      product_temperature: backendOrderItem.product_temperature,
      order_item_id: backendOrderItem.id,
      is_quick_scan: carton.is_quick_scan ?? false,
    };
  });
}

function mergeOrderItemsWithLoadedShippingItems(
  nextOrderItems,
  prevOrderItems,
) {
  return (nextOrderItems ?? []).map((item) => {
    const prevItem = prevOrderItems?.find((p) => p.id === item.id);
    if (prevItem && Array.isArray(prevItem.shippingItems)) {
      return { ...item, shippingItems: prevItem.shippingItems };
    }
    return item;
  });
}

export default function CreateNewShippingForm(props) {
  const {
    shipping,
    setEmptyModelOpen,
    showErrorModal,
    onFetchPackagedItemByBarcode,
    updateShippingApiCall,
    addPackagedItemToOrderItem,
    removeShippedCartonApiCall,
    employeeOptions,
    deleteShipping,
    printShipping,
    isPrintingShipping = false,
    createShippingRecordByID,
    setProductTemperature,
    getOrderItemShipments,
    user_type,
    generateAndPrintShippingLabel,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    invalidOrderItemIds = [],
    quickBooksInvoice,
  } = props;

  const isAdminUser = parseInt(user_type, 10) === 1;
  const invoiceInProgress = Boolean(
    quickBooksInvoice?.polling ||
    quickBooksInvoice?.retrying ||
    quickBooksInvoice?.replacing ||
    (quickBooksInvoice?.status &&
      isActiveInvoiceStatus(quickBooksInvoice.status.status)),
  );

  const [barcodeScanInProgress] = useState(false);
  const [shippingItemsLoading, setShippingItemsLoading] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const barcodeInputRef = useRef(null);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.order_id = null;
    if (values.order_id === 0) {
      temp.order_id = "Please fill out field.";
    }

    temp.shipped_date = null;
    if (!values.shipped_date) {
      temp.shipped_date = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  useEffect(() => {
    if (shipping) {
      const statusValue =
        typeof shipping?.status === "object"
          ? shipping.status?.value
          : (shipping?.status ?? shipping?.legacy_order_status);
      const statusOption = orderStatusOptions.find(
        (option) => option.value === statusValue,
      );

      const shippingCopy = {
        ...shipping,
        status: statusOption,
      };
      setValues((prev) => ({
        ...shippingCopy,
        orderItems: mergeOrderItemsWithLoadedShippingItems(
          shippingCopy.orderItems,
          prev.orderItems,
        ),
      }));
    }
  }, [shipping]);

  const lifecycleStatusValue =
    values.status?.value ??
    values.lifecycle_status ??
    values.legacy_order_status;
  const isShipmentShipped = lifecycleStatusValue === 5;
  const loadedStatusValue =
    typeof shipping?.status === "object"
      ? shipping.status?.value
      : (shipping?.status ?? shipping?.legacy_order_status);
  const isLoadedShipmentShipped = loadedStatusValue === 5;
  // Shipped can be changed back after save. Scanning, prices, and carton
  // removal stay locked until that save succeeds, and also while the dropdown
  // is set to Shipped but not saved yet.
  const isShipmentClosed = isLoadedShipmentShipped || isShipmentShipped;

  function findIndexForOrderItem(product_id) {
    return values.orderItems.findIndex(
      (element) => element.product_id === product_id,
    );
  }

  const updateShipping = async () => {
    if (!validate() || isSaving) return;

    setIsSaving(true);
    try {
      await updateShippingApiCall(values);
    } finally {
      setIsSaving(false);
    }
  };

  const createShipping = async () => {
    await updateShipping();
  };

  const normalItemScanned = async function (
    packagedItem,
    itemIndex,
    productTemperature,
    productCondition,
  ) {
    const orderItems = [...values.orderItems];
    const orderItem = orderItems[itemIndex];
    const condition =
      lineProductConditionOption(productCondition) ??
      lineProductConditionOption(orderItem.product_condition);

    const callResult = await addPackagedItemToOrderItem(
      values.id,
      orderItem,
      packagedItem,
      productTemperature,
      condition,
    );

    if (!callResult?.success) return;

    const updatedOrderItem = callResult?.data?.order_item;
    if (!updatedOrderItem) return;

    const refreshed = await getOrderItemShipments(orderItem.id);
    const refreshedShippingItems = refreshed?.success
      ? refreshed.data
      : normalizeShippingItemsFromBackendOrderItem(updatedOrderItem);

    const orderItemBeingEdited = {
      ...orderItem,
      ...updatedOrderItem,
      shippingItems: refreshedShippingItems,
    };

    const payload = {
      index: itemIndex,
      item: orderItemBeingEdited,
    };
    const newOrderItems = updateObjectInArray(orderItems, payload);
    setValues({
      ...values,
      orderItems: newOrderItems,
      total_cartons: callResult?.data?.total_cartons ?? values.total_cartons,
      scanned_box_label: "",
    });
  };

  const handleBarcodeScan = async function (scannedBarcode) {
    setValues({
      ...values,
      scanned_box_label: "",
    });
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
    let callResult = await onFetchPackagedItemByBarcode(scannedBarcode);
    if (callResult.success) {
      const packagedItem = callResult.data;
      var prodIdToSearch = packagedItem.packaged_item.product_id;
      if (packagedItem.is_skid_label) {
        if (packagedItem.packaged_item.length > 0) {
          prodIdToSearch = packagedItem.packaged_item[0].product_id;
        } else {
          showErrorModal("No items for skid label");
          return;
        }
      }
      const itemIndex = findIndexForOrderItem(prodIdToSearch);

      if (itemIndex < 0) {
        showErrorModal("Item not in order");
        return;
      }

      const orderItem = values.orderItems[itemIndex];
      const productTemperatureNotSet =
        orderItem.product_temperature == null ||
        orderItem.product_temperature === "";
      const productConditionNotSet =
        lineProductConditionOption(orderItem.product_condition) == null;

      if (productTemperatureNotSet || productConditionNotSet) {
        setEmptyModelOpen({
          childComponent: (
            <ProductTemperaturePromptModal
              setOpen={setEmptyModelOpen}
              orderItem={orderItem}
              onConfirm={(temperature, productCondition) => {
                normalItemScanned(
                  packagedItem.packaged_item,
                  itemIndex,
                  temperature,
                  productCondition,
                );
              }}
            />
          ),
          open: true,
        });
      } else {
        normalItemScanned(packagedItem.packaged_item, itemIndex);
      }
    } else {
      showErrorModal("We could not find that item.");
    }
  };

  const makeRemoveShippedCartonApiCall = async function (carton) {
    const product_id = carton.product_id;

    const itemIndex = findIndexForOrderItem(product_id);

    if (itemIndex < 0) return;

    const onItemDeleted = await removeShippedCartonApiCall(carton);
    if (!onItemDeleted?.success) return;

    const orderItems = [...values.orderItems];

    const orderItem = orderItems[itemIndex];

    const updatedOrderItemFromBackend = onItemDeleted?.data?.order_item;
    if (!updatedOrderItemFromBackend) return;

    const refreshed = await getOrderItemShipments(orderItem.id);
    const normalizedShippingItems = refreshed?.success
      ? refreshed.data
      : normalizeShippingItemsFromBackendOrderItem(updatedOrderItemFromBackend);

    const orderItemBeingEdited = {
      ...orderItem,
      ...updatedOrderItemFromBackend,
      shippingItems: normalizedShippingItems,
    };

    var payload = {
      index: itemIndex,
      item: orderItemBeingEdited,
    };
    const newOrderItems = updateObjectInArray(orderItems, payload);
    setValues({ ...values, orderItems: newOrderItems });
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const handleProductReadingsUpdated = (
    index,
    temperature,
    productCondition,
  ) => {
    const updatedItems = values.orderItems.map((item, i) =>
      i === index
        ? {
            ...item,
            product_temperature: temperature,
            product_condition: productCondition,
          }
        : item,
    );
    setValues({ ...values, orderItems: updatedItems });
  };

  const handleOrderItemExpand = async function (orderItem) {
    if (!getOrderItemShipments) return;
    const orderItemId = orderItem.id;
    setShippingItemsLoading((prev) => ({ ...prev, [orderItemId]: true }));
    try {
      const result = await getOrderItemShipments(orderItemId);
      if (result.success && result.data) {
        // Functional update avoids overwriting another row's shippingItems when
        // multiple expands complete out of order (stale closure after await).
        setValues((prev) => {
          const itemIndex = prev.orderItems.findIndex(
            (element) => element.id === orderItemId,
          );
          if (itemIndex < 0) return prev;
          const orderItems = [...prev.orderItems];
          const payload = {
            index: itemIndex,
            item: { ...orderItems[itemIndex], shippingItems: result.data },
          };
          const newOrderItems = updateObjectInArray(orderItems, payload);
          return { ...prev, orderItems: newOrderItems };
        });
      }
    } finally {
      setShippingItemsLoading((prev) => ({ ...prev, [orderItemId]: false }));
    }
  };

  const askDeleteShippingItem = function (item) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => {
            makeRemoveShippedCartonApiCall(item);
          }}
          message={`Are you sure you want to remove ${item.product_name} - ${item.barcode}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const askDeleteShipping = function () {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete shipment?"
          buttonMessage="Delete shipment"
          confirmAction={deleteShipping}
          message={`Are you sure you want to delete shipment #${values.id}? Its scanned inventory will be returned to stock. This action cannot be undone.`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return (
    <div className="h-full">
      <div className="flex flex-col gap-3 text-sm">
        <section className="card-custom space-y-3 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-gray-900">
              Shipping details
            </h2>
            <Pills
              content={shipmentStatusPill(
                values.status ??
                  values.status_label ??
                  values.lifecycle_status ??
                  "Picking in progress",
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Input
              label="Invoice #"
              name="invoice_number"
              onChange={handleInputChange}
              value={values.invoice_number}
              labelOn={true}
              type="text"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-35-custom shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
              error={errors.invoice_number}
            />

            <SelectMenu
              handleInputChange={handleInputChange}
              name="created_by"
              options={employeeOptions}
              value={values.created_by}
              title={"Shipped by"}
              error={errors.created_by}
              showLabel={true}
            />

            <DatePickerInput
              label="Shipped date"
              name="shipped_date"
              onChange={handleInputChange}
              value={values.shipped_date}
              labelOn={true}
              type="text"
              dateFormat="LLL dd, yyyy"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
              error={errors.shipped_date}
            />

            <SelectMenu
              handleInputChange={handleInputChange}
              name="status"
              options={orderStatusOptions.filter(
                (option) => option.value !== -1,
              )}
              value={values.status}
              title={"Status"}
              error={errors.status}
              showLabel={true}
            />
          </div>

          <Accordion className="shipping-qa-accordion">
            <AccordionTab
              header={
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                      <ClipboardDocumentCheckIcon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">
                        QA & truck inspection
                      </span>
                      <span className="block truncate text-xs font-normal text-slate-500">
                        Vehicle inspection
                      </span>
                    </span>
                  </div>
                  <span className="hidden shrink-0 items-center gap-1.5 sm:flex">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                      {values.truck_condition?.label ?? "Truck not checked"}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                      {values.truck_temperature ?? "—"} °C
                    </span>
                  </span>
                </div>
              }
            >
              <p className="mb-4 text-xs leading-5 text-slate-500">
                Record the delivery vehicle inspection. Product condition is
                saved on each order line.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="truck_condition"
                  options={conditionTypes}
                  value={values.truck_condition}
                  title="Truck Condition"
                  error={errors.truck_condition}
                />

                <Input
                  label="Truck Temperature"
                  name="truck_temperature"
                  onChange={handleInputChange}
                  value={values.truck_temperature}
                  labelOn={true}
                  type="text"
                  suffix=" °C"
                  parentClass="max-w-[20rem]"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-35-custom shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
                  error={errors.truck_temperature}
                />
              </div>
            </AccordionTab>
          </Accordion>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="sm:col-span-2 xl:col-span-4">
              <label
                htmlFor="shipping_notes"
                className="block text-sm font-medium text-slate-700"
              >
                Notes
              </label>
              <textarea
                id="shipping_notes"
                name="notes"
                rows={2}
                value={values.notes ?? ""}
                onChange={handleInputChange}
                placeholder="Add handling instructions or shipment notes"
                className="mt-1 block w-full resize-y rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-sky-500 focus:outline-none focus:ring-sky-500"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            {quickBooksInvoice?.error ||
            quickBooksInvoice?.polling ||
            quickBooksInvoice?.retrying ||
            quickBooksInvoice?.replacing ||
            (quickBooksInvoice?.status &&
              quickBooksInvoice.status.status !== "succeeded") ? (
              <div className="w-full text-left">
                <QuickBooksInvoiceStatus
                  status={quickBooksInvoice?.status}
                  loading={quickBooksInvoice?.loading}
                  error={quickBooksInvoice?.error}
                  retrying={quickBooksInvoice?.retrying}
                  replacing={quickBooksInvoice?.replacing}
                  onRetry={quickBooksInvoice?.retry}
                  onReplace={quickBooksInvoice?.replace}
                />
              </div>
            ) : null}
            <button
              type="button"
              disabled={isPrintingShipping}
              className={classNames(
                values.id > 0 ? null : "hidden",
                "inline-flex items-center justify-center gap-2 rounded-md bg-gray-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 disabled:cursor-wait disabled:opacity-70",
              )}
              onClick={() => printShipping()}
            >
              {isPrintingShipping ? (
                <>
                  <ArrowPathIcon
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  Printing…
                </>
              ) : (
                "Print"
              )}
            </button>
            <button
              type="button"
              disabled={isSaving || invoiceInProgress}
              className="inline-flex min-w-[5rem] items-center justify-center gap-2 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 disabled:cursor-wait disabled:opacity-70"
              onClick={createShipping}
            >
              {isSaving ? (
                <>
                  <ArrowPathIcon
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
            {isAdminUser && values.quickbooks_invoice_id > 0 ? (
              <a
                href={`https://qbo.intuit.com/app/invoice?txnId=${values.quickbooks_invoice_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
              >
                View Invoice
              </a>
            ) : null}
            {isAdminUser ? (
              <button
                type="button"
                className={classNames(
                  values.id > 0 ? null : "hidden",
                  "inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700",
                )}
                onClick={askDeleteShipping}
              >
                Delete
              </button>
            ) : null}
            <button
              type="button"
              className="inline-flex justify-center rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-600"
              onClick={() => createShippingRecordByID()}
            >
              Print Shipping Record
            </button>
          </div>
        </section>

        <section className="card-custom p-2">
          <div className="flex flex-col gap-3 px-1 pb-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Order items
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Scanning sells the carton immediately, and it can still be
                removed until the shipment is marked Shipped.
              </p>
            </div>
            <div className="w-full sm:max-w-xl">
              <label
                htmlFor="scanned_box_label"
                className="block text-sm font-medium text-gray-900"
              >
                Scan barcode
              </label>
              <input
                id="scanned_box_label"
                placeholder="Scan box label"
                name="scanned_box_label"
                disabled={barcodeScanInProgress || isShipmentClosed}
                ref={barcodeInputRef}
                onChange={handleInputChange}
                value={values.scanned_box_label ?? ""}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleBarcodeScan(e.target.value);
                }}
                type="search"
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-35-custom text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>
          </div>

          <OrderItemsForShipping
            setProductTemperature={setProductTemperature}
            askDeleteShippingItem={askDeleteShippingItem}
            orderItems={values.orderItems}
            onExpandRow={handleOrderItemExpand}
            shippingItemsLoading={shippingItemsLoading}
            onProductReadingsUpdated={handleProductReadingsUpdated}
            user_type={user_type}
            onPrintShippingLabel={generateAndPrintShippingLabel}
            setEmptyModelOpen={setEmptyModelOpen}
            onCreatePriceSplit={onCreatePriceSplit}
            onUpdatePriceSplit={onUpdatePriceSplit}
            onDeletePriceSplit={onDeletePriceSplit}
            canEditPrices={isAdminUser && !isShipmentClosed}
            canRemoveItems={!isShipmentClosed}
            invalidOrderItemIds={invalidOrderItemIds}
          />
        </section>
      </div>
    </div>
  );
}
